import React, { useState } from 'react';
import { FiSave, FiUpload, FiX, FiTag, FiPlus } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useUser } from './UserContext';

const BlogEditor = ({ mode = 'create', initialPost = null }) => {
  const [title, setTitle] = useState(initialPost?.title || '');
  const [content, setContent] = useState(initialPost?.content || '');
  const [tags, setTags] = useState(initialPost?.tags || []);
  const [newTag, setNewTag] = useState('');
  const [coverImage, setCoverImage] = useState(initialPost?.cover_image || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { user } = useUser();
  const navigate = useNavigate();

  const API_BASE_URL = 'https://arush1234sharma-querio-backend.hf.space/api/blog';

  const styles = {
    container: {
      backgroundColor: '#000000',
      color: '#ffffff',
      padding: '2rem',
      borderRadius: '8px'
    },
    input: {
      backgroundColor: '#1a1a1a',
      color: '#ffffff',
      border: '1px solid #333',
      borderRadius: '4px',
      padding: '0.75rem',
      width: '100%',
      marginBottom: '1rem'
    },
    textarea: {
      backgroundColor: '#1a1a1a',
      color: '#ffffff',
      border: '1px solid #333',
      borderRadius: '4px',
      padding: '0.75rem',
      width: '100%',
      minHeight: '300px',
      marginBottom: '1rem'
    },
    tag: {
      backgroundColor: '#333',
      color: '#fff',
      padding: '0.25rem 0.5rem',
      borderRadius: '4px',
      marginRight: '0.5rem',
      marginBottom: '0.5rem',
      display: 'inline-flex',
      alignItems: 'center'
    },
    button: {
      backgroundColor: '#4f46e5',
      color: 'white',
      padding: '0.5rem 1rem',
      borderRadius: '4px',
      border: 'none',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      marginRight: '0.5rem'
    }
  };

  const handleAddTag = () => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      setTags([...tags, newTag.trim()]);
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API_BASE_URL}/upload-cover`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user?.token}`
        },
        body: formData
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to upload image');
      }
      
      const data = await response.json();
      setCoverImage(data.file_path);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const url = mode === 'create' 
        ? `${API_BASE_URL}/create` 
        : `${API_BASE_URL}/update/${initialPost.id}`;
      
      const response = await fetch(url, {
        method: mode === 'create' ? 'POST' : 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user?.token}`
        },
        body: JSON.stringify({
          title,
          content,
          tags,
          cover_image: coverImage
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save post');
      }

      const result = await response.json();
      navigate(`/blog/post/${result.id}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <h1 className="text-2xl font-bold mb-6">
        {mode === 'create' ? 'Create New Blog Post' : 'Edit Blog Post'}
      </h1>
      
      {error && (
        <div className="bg-red-900 text-white p-3 rounded mb-4">
          {error}
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        <div className="mb-6">
          <label className="block text-gray-300 mb-2">Title</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            style={styles.input}
            required
          />
        </div>
        
        <div className="mb-6">
          <label className="block text-gray-300 mb-2">Cover Image</label>
          <input
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            style={{ display: 'none' }}
            id="cover-upload"
          />
          <label htmlFor="cover-upload" style={styles.button}>
            <FiUpload className="mr-2" /> Upload Cover
          </label>
          {coverImage && (
            <div className="mt-2">
              <img 
                src={coverImage} 
                alt="Cover preview" 
                className="max-w-xs max-h-40 object-cover"
              />
              <button
                type="button"
                onClick={() => setCoverImage(null)}
                style={{ ...styles.button, backgroundColor: '#ef4444' }}
                className="ml-2"
              >
                <FiX className="mr-1" /> Remove
              </button>
            </div>
          )}
        </div>
        
        <div className="mb-6">
          <label className="block text-gray-300 mb-2">Content (Markdown supported)</label>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            style={styles.textarea}
            required
          />
        </div>
        
        <div className="mb-6">
          <label className="block text-gray-300 mb-2">Tags</label>
          <div className="flex mb-2">
            <input
              type="text"
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              style={styles.input}
              placeholder="Add tag"
              className="flex-grow"
            />
            <button
              type="button"
              onClick={handleAddTag}
              style={styles.button}
            >
              <FiPlus className="mr-1" /> Add
            </button>
          </div>
          <div className="flex flex-wrap">
            {tags.map(tag => (
              <span key={tag} style={styles.tag}>
                <FiTag className="mr-1" size={12} />
                {tag}
                <button
                  type="button"
                  onClick={() => handleRemoveTag(tag)}
                  className="ml-2 text-gray-400 hover:text-white"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>
        
        <div className="flex justify-end">
          <button
            type="submit"
            style={styles.button}
            disabled={loading}
          >
            <FiSave className="mr-2" />
            {loading ? 'Saving...' : mode === 'create' ? 'Publish Post' : 'Update Post'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BlogEditor;